import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InvalidStateError } from '@condo/domain';
import { isAuthenticatedRamConfig } from '@/shared/env/runtime-config';
import { FeatureFlag } from '@/shared/feature-flags';
import { queryClient } from '@/shared/lib/query-client';
import { authManager } from '@/shared/utils/auth/auth-manager';
import { ConfigContext } from '@/shared/utils/config/config-context';
import { syncRamUiLanguage } from '@/shared/utils/language-persistence';
import type { IRamRuntimeConfig } from '@/shared/env/types';

export interface ConfigProviderProps {
    children: ReactNode;
}

export async function fetchRamConfig(url: '/config' | '/config/authed', init?: RequestInit): Promise<IRamRuntimeConfig> {
    const response = await fetch(url, init);
    if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
    }

    return response.json();
}

/** Loads public config first; authenticated config is requested after Firebase resolves. */
export function ConfigProvider({ children }: ConfigProviderProps) {
    const [configScope, setConfigScope] = useState<'public' | 'authenticated'>('public');
    const publicQuery = useQuery(
        {
            queryKey: ['ram-config', 'public'],
            queryFn: () => fetchRamConfig('/config'),
            enabled: false,
            retry: false,
            networkMode: 'always',
            gcTime: 0,
        },
        queryClient,
    );
    const authenticatedQuery = useQuery(
        {
            queryKey: ['ram-config', 'authenticated'],
            queryFn: async () => {
                const token = await authManager.getToken(false);
                const config = await fetchRamConfig('/config/authed', {
                    credentials: 'include',
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!isAuthenticatedRamConfig(config)) {
                    throw new InvalidStateError('Authenticated config response is incomplete');
                }
                return config;
            },
            enabled: false,
            retry: false,
            networkMode: 'always',
            gcTime: 0,
        },
        queryClient,
    );
    const { refetch: refetchPublic } = publicQuery;
    const { refetch: refetchAuthenticated } = authenticatedQuery;

    const loadPublicConfig = useCallback(async () => {
        setConfigScope('public');
        await refetchPublic({ cancelRefetch: false });
    }, [refetchPublic]);
    const loadAuthenticatedConfig = useCallback(async () => {
        setConfigScope('authenticated');
        await refetchAuthenticated({ cancelRefetch: false });
    }, [refetchAuthenticated]);

    const activeQuery = configScope === 'authenticated' ? authenticatedQuery : publicQuery;
    const config = activeQuery.data ?? publicQuery.data ?? null;

    useEffect(() => {
        void loadPublicConfig();
    }, [loadPublicConfig]);

    useEffect(() => {
        if (config) {
            syncRamUiLanguage(isAuthenticatedRamConfig(config) && config[FeatureFlag.F_CHANGE_LANGUAGE_ENABLED] === true);
        }
    }, [config]);

    return (
        <ConfigContext.Provider
            value={{
                config,
                loading: publicQuery.isPending,
                isLoadingAuthenticatedConfig: configScope === 'authenticated' && authenticatedQuery.isFetching,
                error: activeQuery.error?.message ?? null,
                loadPublicConfig,
                loadAuthenticatedConfig,
            }}
        >
            {children}
        </ConfigContext.Provider>
    );
}
